import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { AppDispatch } from "../../../../dataManagement/uiDb/UiDb";
import {
  createListingCollection,
  updateListingCollection,
} from "../../../../services/thunks/ListingCollectionThunk";
import {
  selectCreateListingCollectionStatus,
  selectUpdateListingCollectionStatus,
} from "../../../../dataManagement/uiDb/ListingCollectionDb";
import { ListingCollectionDetail } from "../../../../interfaces/ListingCollectionObject";
import StorageApiService from "../../../../services/apiServices/StorageApiService";
import { COLLECTION_NAME_MAX_LENGTH } from "../../../shared/SaveAsCollectionModal/SaveAsCollectionModal";
import mediaUtility from "../../../../services/utility/MediaUtility";
import { useNotification } from "../../../shared/contexts/NotificationContext";
import { NotificationType } from "../../../../constants/Enums";
import useScrollLock from "../../../../hooks/useScrollLock";
import CollectionBannerUploader from "../BusinessCollections/childComponents/CollectionBannerUploader";
import BusinessSearchAddListings, { StagedListing } from "./childComponents/BusinessSearchAddListings";
import "./styles/CreateListingCollectionModal.css";

interface CreateListingCollectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Present in edit mode — the collection plus its resolved member listings. */
  editCollection?: ListingCollectionDetail | null;
  /**
   * Fires after a successful save, before onClose. Callers that render the
   * product grid (ListingCollectionPage) pass a refetch here so the grid
   * stays current without a full page reload.
   */
  onSaved?: () => void;
}

const CreateListingCollectionModal: React.FC<CreateListingCollectionModalProps> = ({
  isOpen,
  onClose,
  editCollection = null,
  onSaved,
}) => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const { triggerNotification } = useNotification();

  const { isSubmitting: isCreating } = useSelector(selectCreateListingCollectionStatus);
  const { isSubmitting: isUpdating } = useSelector(selectUpdateListingCollectionStatus);
  const isSubmitting = isCreating || isUpdating;

  const isEditMode = !!editCollection;

  const [name, setName] = useState("");
  const [existingBannerUrl, setExistingBannerUrl] = useState<string | null>(null);
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [isUploadingBanner, setIsUploadingBanner] = useState(false);
  const [stagedListings, setStagedListings] = useState<StagedListing[]>([]);

  useScrollLock(isOpen);

  useEffect(() => {
    if (!isOpen) return;
    if (editCollection) {
      setName(editCollection.collection.name);
      setExistingBannerUrl(editCollection.collection.bannerUrl ?? null);
      // Map expanded products to StagedListing for the chip display.
      setStagedListings(
        editCollection.products.map((p) => ({
          productId: p.productId,
          title: p.title ?? "Unnamed listing",
          // businessName not available from the detail response — omit.
        }))
      );
    } else {
      setName("");
      setExistingBannerUrl(null);
      setStagedListings([]);
    }
    setBannerFile(null);
  }, [isOpen, editCollection]);

  const validate = (): boolean => {
    if (!name.trim()) {
      triggerNotification("Collection name is required.", NotificationType.ERROR);
      return false;
    }
    if (!bannerFile && !existingBannerUrl) {
      triggerNotification("Add a banner image.", NotificationType.ERROR);
      return false;
    }
    if (stagedListings.length === 0) {
      triggerNotification(
        "Add at least one listing to the collection.",
        NotificationType.ERROR
      );
      return false;
    }
    return true;
  };

  const uploadBanner = async (file: File): Promise<string> => {
    const contentType = mediaUtility.resolveContentType(file);
    if (!contentType) throw new Error("This image type isn't supported.");
    setIsUploadingBanner(true);
    try {
      const target = await StorageApiService.requestUpload({
        fileName: file.name,
        folder: "business",
        contentType,
      });
      await StorageApiService.uploadToTarget(target, file, contentType);
      return target.publicUrl;
    } finally {
      setIsUploadingBanner(false);
    }
  };

  const handleSubmit = async () => {
    if (!validate() || isSubmitting || isUploadingBanner) return;

    try {
      const bannerUrl = bannerFile
        ? await uploadBanner(bannerFile)
        : (existingBannerUrl as string);

      const payload = {
        collectionName: name,
        productIds: stagedListings.map((l) => l.productId),
        bannerUrl,
      };

      if (isEditMode && editCollection) {
        await dispatch(
          updateListingCollection({ uid: editCollection.collection.uid, ...payload })
        ).unwrap();
        triggerNotification("Collection updated!", NotificationType.SUCCESS);
        onSaved?.();
        onClose();
      } else {
        const created = await dispatch(createListingCollection(payload)).unwrap();
        triggerNotification("Collection created!", NotificationType.SUCCESS);
        onSaved?.();
        onClose();
        navigate(`/listing-collection/${created.uid}`, {
          state: { collection: created },
        });
      }
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "Something went wrong. Please try again.";
      triggerNotification(msg, NotificationType.ERROR);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="create-lst-coll-overlay" onClick={onClose}>
      <div className="create-lst-coll-modal" onClick={(e) => e.stopPropagation()}>
        <div className="create-lst-coll-header">
          <button
            type="button"
            className="create-lst-coll-close"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={20} />
          </button>
          <h2 className="create-lst-coll-title">
            {isEditMode ? "Edit Collection" : "Create Collection"}
          </h2>
          <div style={{ width: "2.125rem" }} />
        </div>

        <div className="create-lst-coll-body">
          <div className="create-lst-coll-field">
            <label className="create-lst-coll-label">Name</label>
            <input
              type="text"
              className="create-lst-coll-input"
              placeholder="e.g. Best Handmade Bags"
              value={name}
              maxLength={COLLECTION_NAME_MAX_LENGTH}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="create-lst-coll-field">
            <label className="create-lst-coll-label">Banner Image</label>
            <CollectionBannerUploader
              existingBannerUrl={existingBannerUrl}
              onFileSelected={setBannerFile}
              onInvalidFile={(reason) =>
                triggerNotification(reason, NotificationType.ERROR)
              }
              isUploading={isUploadingBanner}
            />
          </div>

          <div className="create-lst-coll-field">
            <label className="create-lst-coll-label">Add Listings via Business</label>
            <BusinessSearchAddListings
              stagedListings={stagedListings}
              onListingsChange={setStagedListings}
            />
          </div>
        </div>

        <div className="create-lst-coll-footer">
          <button
            type="button"
            className="create-lst-coll-submit"
            onClick={handleSubmit}
            disabled={isSubmitting || isUploadingBanner}
          >
            {isUploadingBanner
              ? "Uploading banner..."
              : isSubmitting
              ? isEditMode
                ? "Saving..."
                : "Creating..."
              : isEditMode
              ? "Save Changes"
              : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateListingCollectionModal;
